import { Component, Input, OnInit } from '@angular/core';

import { delay, Observable } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';

import { SentryLogger } from '@fcom/core';

import { ProgressCircleColor } from './enums';

/**
 * This component can be used to display progress towards a goal.
 *
 * @example
 * <fcom-progress-circle
 *   [progress]="50"
 *   [radius]="90"
 *   [color]="ProgressCircleColor.BLUE"
 *   [ariaLabel]="'descriptive.text' | finLocalization">
 *   {{ progress }} %
 * </fcom-progress-circle>
 */

const RADIUS = 90;
const BAR_WIDTH = 6;
const PATH_WIDTH = BAR_WIDTH - 2;

@Component({
  selector: 'fcom-progress-circle',
  templateUrl: './progress-circle.component.html',
  styleUrls: ['./progress-circle.component.scss'],
})
export class ProgressCircleComponent implements OnInit {
  readonly BAR_WIDTH = BAR_WIDTH;
  readonly PATH_WIDTH = PATH_WIDTH;

  /**
   * The current progress percentage
   */
  @Input()
  progress$: Observable<number>;

  /**
   * Circle radius size
   */
  @Input()
  radius: number = RADIUS;

  /**
   * Color of the progress bar
   */
  @Input()
  color: ProgressCircleColor = ProgressCircleColor.BLUE;

  /**
   * Descriptive label for screen readers
   */
  @Input({ required: true })
  ariaLabel: string;

  stroke$: Observable<number>;
  size: number;
  viewBox: string;
  strokeDasharray: number;
  position: number;

  constructor(private logger: SentryLogger) {}

  ngOnInit(): void {
    this.strokeDasharray = +(2 * Math.PI * this.radius).toFixed(2);
    this.size = this.radius * 2 + BAR_WIDTH;
    this.viewBox = `0 0 ${this.size} ${this.size}`;
    this.position = this.radius + BAR_WIDTH / 2;

    this.stroke$ = this.progress$.pipe(
      delay(0), //delay so that the progress is animated from 0
      tap((progress) => {
        if (progress < 0 || progress > 100) {
          this.logger.warn(`out of range progress ${progress}% in ProgressCircleComponent`);
        }
      }),
      map((progress) => ((100 - progress) / 100) * this.strokeDasharray),
      startWith(this.strokeDasharray)
    );
  }
}
